// Copyright 2025, University of Maryland, All Rights Reserved
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { FileText, BookOpen } from "lucide-react"
import { type DatasourceBill, type FormattingData } from '@/types/datasource-bill'
import { type ProcessedAmendmentData } from './types'
import { useGetDatasourceFileContent, useDownloadFile } from '@/api/datasource-bills-api'
import { useState } from 'react'
import * as React from 'react'
import { useToast } from '@/hooks/use-toast'
import { BillFullTextDialog } from './BillFullTextDialog'
import { BillVersionSelector } from './BillVersionSelector'
import { BillSummaryCard } from './BillSummaryCard'
import { VersionDetailsCard } from './VersionDetailsCard'
import { AmendmentsTabContent } from './AmendmentsTabContent'

interface BillVersionsDialogProps {
  bill: DatasourceBill | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BillVersionsDialog({ bill, open, onOpenChange }: BillVersionsDialogProps) {
  const [formattingData, setFormattingData] = useState<Record<number, FormattingData>>({})
  const [processedAmendments, setProcessedAmendments] = useState<Record<number, ProcessedAmendmentData>>({})
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<string>("overview")
  const [fullTextDialogOpen, setFullTextDialogOpen] = useState(false)
  const fileContentMutation = useGetDatasourceFileContent()
  const downloadFileMutation = useDownloadFile()
  const { toast } = useToast()

  // Process formatting data into amendment data
  const processFormattingIntoAmendments = (formatting: FormattingData, versionNumber: number): ProcessedAmendmentData => {
    const amendments: ProcessedAmendmentData['amendments'] = []
    let strikeout_count = 0
    let underline_count = 0
    let italics_count = 0
    const pages_with_amendments = new Set<number>()

    Object.entries(formatting).forEach(([pageKey, pageTexts]) => {
      const page_number = parseInt(pageKey.replace('page_', ''))
      
      pageTexts.forEach(textItem => {
        const amendmentTypes: ('strikeout' | 'underline' | 'italics')[] = []
        
        if (textItem.formatting.includes('strike')) {
          amendmentTypes.push('strikeout')
          strikeout_count++
        }
        if (textItem.formatting.includes('underline')) {
          amendmentTypes.push('underline')
          underline_count++
        }
        if (textItem.formatting.includes('italics')) {
          amendmentTypes.push('italics')
          italics_count++
        }
        
        // Only add if there are amendment types
        if (amendmentTypes.length > 0) {
          amendments.push({
            types: amendmentTypes,
            text: textItem.text,
            page_number,
            line_number: textItem.line_number
          })
          pages_with_amendments.add(page_number)
        }
      })
    })

    return {
      bill_number: bill?.bill_number || '',
      session_code: bill?.session_code || '',
      version_number: versionNumber,
      total_pages: Object.keys(formatting).length,
      summary: {
        total_amendments: strikeout_count + underline_count + italics_count,
        strikeout_count,
        underline_count,
        italics_count,
        pages_with_amendments: pages_with_amendments.size
      },
      amendments
    }
  }

  // Initialize selected version to the first version
  const initializeSelectedVersion = React.useCallback(() => {
    if (bill && selectedVersion === null && bill.versions.length > 0) {
      setSelectedVersion(bill.versions[0].version)
    }
  }, [bill, selectedVersion])

  // Reset state when bill changes
  React.useEffect(() => {
    if (bill) {
      setSelectedVersion(null)
      setFormattingData({})
      setProcessedAmendments({})
      setActiveTab("overview")
    }
  }, [bill?.bill_number])

  // Call initialization when dialog opens or bill changes
  React.useEffect(() => {
    if (open && bill) {
      initializeSelectedVersion()
    }
  }, [open, bill, initializeSelectedVersion])

  const handleViewAmendments = React.useCallback(async (versionNumber: number, switchToTab: boolean = false) => {
    if (!bill) return
    
    const version = bill.versions.find(v => v.version === versionNumber)
    if (!version) {
      toast({
        title: "Version Not Found", 
        description: "The requested version could not be found.",
        variant: "destructive"
      })
      return
    }

    // Check if this version has amendments detected
    if (!version.amendments_detected) {
      // If no amendments, create empty amendment data and switch to tab if requested
      const emptyAmendmentData = {
        bill_number: bill.bill_number,
        session_code: bill.session_code,
        version_number: versionNumber,
        total_pages: 0,
        summary: {
          total_amendments: 0,
          strikeout_count: 0,
          underline_count: 0,
          italics_count: 0,
          pages_with_amendments: 0
        },
        amendments: []
      }
      
      setProcessedAmendments(prev => ({
        ...prev,
        [versionNumber]: emptyAmendmentData
      }))
      
      if (switchToTab) {
        setActiveTab("amendments")
      }
      return
    }

    // Check if formatting data is available for versions with amendments
    if (!version.formatting_s3_key) {
      toast({
        title: "No Formatting Data Available", 
        description: "Formatting data is not available for this version.",
        variant: "destructive"
      })
      return
    }

    // Check if we already have the data
    if (formattingData[versionNumber]) {
      if (switchToTab) {
        setActiveTab("amendments")
      }
      return
    }

    // Prevent duplicate fetches for the same version
    if (fileContentMutation.isPending) {
      return
    }

    try {
      const content = await fileContentMutation.mutateAsync(version.formatting_s3_key)
      const formatting: FormattingData = JSON.parse(content)
      
      setFormattingData(prev => ({
        ...prev,
        [version.version]: formatting
      }))

      // Process the formatting data into amendment data
      const amendmentData = processFormattingIntoAmendments(formatting, versionNumber)
      setProcessedAmendments(prev => ({
        ...prev,
        [version.version]: amendmentData
      }))
      
      // Only switch to amendments tab if explicitly requested
      if (switchToTab) {
        setActiveTab("amendments")
      }
    } catch (error) {
      console.error('Failed to fetch formatting data:', error)
      toast({
        title: "Failed to Load Formatting Data",
        description: "Could not load formatting data.",
        variant: "destructive"
      })
    }
  }, [bill, fileContentMutation, toast, formattingData])

  // Auto-load formatting data when switching to amendments tab with a selected version
  React.useEffect(() => {
    if (bill && activeTab === "amendments" && selectedVersion) {
      const selectedVersionData = bill.versions.find(v => v.version === selectedVersion)
      if (selectedVersionData && 
          !processedAmendments[selectedVersion] &&
          !fileContentMutation.isPending) {
        handleViewAmendments(selectedVersion)
      }
    }
  }, [activeTab, selectedVersion, processedAmendments, bill, handleViewAmendments, fileContentMutation.isPending])

  // Early return after all hooks have been called
  if (!bill) return null

  const handleVersionChange = (versionNumber: number) => {
    setSelectedVersion(versionNumber)
    // If switching to a version with formatting and we're on the amendments tab, load the data
    if (activeTab === "amendments") {
      const version = bill.versions.find(v => v.version === versionNumber)
      if (version?.formatting_s3_key && !formattingData[versionNumber] && !fileContentMutation.isPending) {
        handleViewAmendments(versionNumber)
      }
    }
  }

  const handleViewFullText = (version: any) => {
    if (!version.text_s3_key) {
      toast({
        title: "No Text Available",
        description: "Full text is not available for this version.",
        variant: "destructive"
      })
      return
    }
    setFullTextDialogOpen(true)
  }

  const handleDownloadPdf = async (version: any) => {
    if (!version.pdf_s3_key) {
      toast({
        title: "No PDF Available",
        description: "PDF is not available for this version.",
        variant: "destructive"
      })
      return
    }

    try {
      const filename = `${bill?.bill_number}_v${version.version}.pdf`
      await downloadFileMutation.mutateAsync({ 
        s3_key: version.pdf_s3_key, 
        filename 
      })
      toast({
        title: "Download Started",
        description: `Downloading ${filename}`,
        variant: "default"
      })
    } catch (error) {
      console.error('Failed to download PDF:', error)
      toast({
        title: "Download Failed",
        description: "Failed to download PDF. Please try again.",
        variant: "destructive"
      })
    }
  }

  // Calculate statistics for display
  const versionsWithAmendments = bill.versions.filter(v => v.amendments_detected)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Versions & Amendments - {bill.bill_number}
            </DialogTitle>
            
            <BillVersionSelector 
              versions={bill.versions}
              selectedVersion={selectedVersion}
              onVersionChange={handleVersionChange}
            />
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="amendments" className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Amendments
                {versionsWithAmendments.length > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                    {versionsWithAmendments.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="flex-1">
              <ScrollArea className="h-full max-h-[70vh]">
                <div className="space-y-4 pr-4">
                  <BillSummaryCard bill={bill} />

                  {/* Selected Version Details */}
                  {selectedVersion && (() => {
                    const version = bill.versions.find(v => v.version === selectedVersion)
                    if (!version) return null

                    const versionAmendmentData = processedAmendments[selectedVersion]

                    return (
                      <VersionDetailsCard
                        version={version}
                        versionAmendmentData={versionAmendmentData}
                        onViewFullText={handleViewFullText}
                        onDownloadPdf={handleDownloadPdf}
                        onViewAmendments={handleViewAmendments}
                        isDownloadPending={downloadFileMutation.isPending}
                        isAmendmentsPending={fileContentMutation.isPending && selectedVersion === version.version}
                      />
                    )
                  })()}

                  {/* No Version Selected */}
                  {!selectedVersion && (
                    <Card>
                      <CardContent className="text-center py-8">
                        <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-2">No Version Selected</h3>
                        <p className="text-muted-foreground">
                          Select a version from the header to view detailed information.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="amendments" className="flex-1">
              <ScrollArea className="h-full max-h-[70vh]">
                <div className="pr-4">
                  <AmendmentsTabContent
                    bill={bill}
                    selectedVersion={selectedVersion}
                    processedAmendments={processedAmendments}
                  />
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Full Text Dialog */}
      <BillFullTextDialog
        bill={bill}
        open={fullTextDialogOpen}
        onOpenChange={setFullTextDialogOpen}
      />
    </>
  )
}
